<template>
  <div :id="`container-${index + 1}`" class="hypo-asset-allocation-horizontal-bar-chart">
    <mds-layout-grid gutters="none">
      <mds-row>
        <mds-col :cols="2">
          <div class="chart-labels">
            <span v-for="label in chartData.bars" :key="label.name" class="chart-labels__item">{{
              label.name
            }}</span>
          </div>
        </mds-col>
        <mds-col :cols="10">
          <div :id="`aaChart${index + 1}`" class="asset-allocation-chart-component"></div>
        </mds-col>
      </mds-row>
    </mds-layout-grid>
  </div>
</template>

<script>
import d3 from 'd3';
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import HorizontalBarChart from 'mbc/dist/mbc-horizontal-bar';
import { MdsLayoutGrid, MdsRow, MdsCol } from '@mds/layout-grid';
import { hasBenchmark } from './utils/assetAllocationUtils';
import defaultConfig from './_config';

export default {
  name: 'hypo-asset-allocation-horizontal-bar-chart',
  components: { MdsLayoutGrid, MdsRow, MdsCol },
  mixins: [MwcVueHelperMixin, MwcConfigMixin],
  props: {
    mwcId: {
      type: String,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    config: {
      type: [String, Object],
      default: () => {},
    },
    modelData: {
      type: [String, Object],
    },
    boundaries: {
      type: [Object],
    },
    isSinglePortfolio: {
      type: Boolean,
    },
  },
  chartInstance: null,
  data() {
    return {
      defaultConfig: {
        intlNamespace: this.mwcId,
        ...defaultConfig,
      },
      chartData: {},
    };
  },
  computed: {
    labels() {
      return {
        title: this.translate('title'),
        timeLabel: this.translate('timeLabel'),
      };
    },
  },
  watch: {
    modelData: {
      handler() {
        if (!hasBenchmark(this.modelData)) {
          this.$options.chartInstance.removeBench();
        }
        this.setAssetAllocationData(this.modelData);
        this.$options.chartInstance.updateData(this.chartData);
      },
    },
  },
  mounted() {
    this.setAssetAllocationData(this.modelData);
    this.initChart(this.index);
    window.addEventListener('resize', this.resizeHandler, false);
    window.addEventListener('scroll', this.scrollHandler, false);
    if (!hasBenchmark(this.modelData)) {
      this.$options.chartInstance.removeBench();
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeHandler, false);
    window.removeEventListener('scroll', this.scrollHandler, false);
  },
  methods: {
    initChart(index) {
      const container = d3.select(`#aaChart${index + 1}`);
      const chartConfig = {
        xAxisOrient: 'top',
        legendFundName: this.formatLabel('Portfolio'),
        legendBenchmarkName: this.formatLabel('Benchmark'),
        toolTipLabel1: 'Value',
        toolTipUseStaticHeader: false,
        xAxisMinMaxIntervalValues: this.boundaries,
        benchMarksVisible: true,
      };
      this.$options.chartInstance = new HorizontalBarChart(chartConfig);
      container.datum(this.chartData).call(this.$options.chartInstance.init());
    },
    setAssetAllocationData(modelData) {
      if (modelData) {
        this.chartData = {
          bars: modelData.portfolio,
          benchmarks: modelData.benchmark,
        };
      }
    },
    resizeHandler() {
      this.$options.chartInstance.update();
    },
    scrollHandler() {
      this.$options.chartInstance.update();
    },
    formatLabel(label) {
      return this.isSinglePortfolio ? label : `${label} ${this.index + 1}`;
    },
  },
};
</script>

<style lang="scss">
@import '~mbc/dist/mbc-horizontal-bar';
</style>

<style lang="scss" scoped>
@import '~@mds/constants';
@import '~@mds/typography';
@import './styles/variables';

.#{$component-ns} {
  background-color: $mds-background-color-white;
  text-align: center;
}
.asset-allocation-chart-component {
  position: relative;
  left: -25px;
  height: 100%;
  width: 100%;
  ::v-deep .mbc-chart-legend {
    margin: 0px 30px -19px 40px !important;
  }
}
.chart-labels {
  text-align: right;
  padding: 50px 0 10px 0;
  &__item {
    display: block;
    white-space: nowrap;
    font-family: $mds-typography-font-family-sans-serif;
    font-size: 14px;
    line-height: 19px;
    padding: 0 0 2px 0;
    overflow: hidden;
    text-overflow: ellipsis;
    text-transform: capitalize;
  }
}
</style>
